import { useCallback, useState } from 'react';

import {
  Paper,
  Table as MuiTable,
  TableRowProps as MuiRowProps,
  TableContainer,
  TableFooter,
  TablePagination,
  TableRow,
} from '@mui/material';
import clsx from 'clsx';

import { useTableStyles } from './Table.styles';
import { TableColumn, TableFilterItem, TableSearchTypes, TableSuggestions } from './Table.types';
import { TableBody } from './TableBody/TableBody';
import { TableHead } from './TableHead';

export interface TableProps {
  /** Padding for each table cell on pixels */
  cellPadding?: number | string;

  /** Array with objects used to render the columns */
  columns: Array<TableColumn>;

  /** Table height CSS property. Eg 'auto', '100%', '100em' */
  height?: string | number;

  /** Table maxHeight CSS property. Eg 'auto', '100%', '100em' */
  maxHeight?: string | number;

  /** Controls if the loader is shown */
  loading?: boolean;

  /** Executed when the user changes the page of the table. */
  onChangePage?: (page: number) => void;

  /** Executed when the user changes the number of rows per page */
  onChangeRowsPerPage?: (rows: number) => void;

  /** Executed when the user filters the results in a column
   *
   * - if *filterOnType* is true the function will be executed when the value
   *   of the filter text is at least 3 characters long.
   * - if *filterOnType* is false the function will be executed when the user
   *   presses enter or clicks the "Go" button.
   * */
  onFilterBy?: (value: any) => void;

  /** Executed when the user sorts the table by one of its columns */
  onRequestSort?: (columnId: any) => void;

  /** Executed when the user triggers a search */
  onSearch?: (value: any) => void;

  onEditCell?: (columnId: string | number, value: any, row: Record<string, any>) => Promise<any>;

  /** Optional externally controlled filters used by the header chip state */
  controlledFilters?: Array<TableFilterItem>;

  /** Rows data to be rendered */
  rows: Array<any>;

  /** Total number of rows.
   *  If no value or -1 is provided the table assumes that the total number of rows * is unknown
   */
  rowsCount?: number;

  /** Selected number of rows per page */
  rowsPerPage?: number;

  /** Array of positive integers indicating the number of rows per page the user can set.  */
  rowsPerPageOptions?: Array<number>;

  /** Current page */
  page?: number;

  /** Defines the kind of input to be used on the table head */
  searchType?: TableSearchTypes;

  /**  Suggestions that should appear in the autocomplete popup */
  suggestions?: TableSuggestions;

  /** Table size */
  size?: 'medium' | 'small';

  /** Gives option to hide the footer with the paging option
   * Defaut value - false
   */
  footerHidden?: boolean;

  /** Gives option to hide the header of the table
   * Defaut value - false
   */
  headerHidden?: boolean;

  /** Funtion which takes a single parameter of type row and returns the id of that row
   * If not defined - the table assumes that the id of the row is stored under the id key
   * Example 1: {id: 1, name: "John"}
   * No need to define idLocator
   * Example 2: {fixtureId: 1, competitors: ["Manchester United", "Liverpool"]}
   * Need to define idLocator -> idLocator={fixture => fixture.fixtureId}
   */
  idLocator?: (param: any) => any;
  //class name for overriding styles
  className?: string;
  //Simple property to pass class names to the component's pagination for styling purposes.
  paginationAlignment?: 'left' | 'right';
  //this param is used to save things in the local storage, per page
  localStorageKey: string;
  //sticky footer
  stickyFooter?: boolean;
  //toggle on hover color change for all rows
  hover?: MuiRowProps['hover'];
  //toggle on hover color change for expandable rows only
  hoverForExpandableRow?: boolean;

  /** Enables optional multi-row selection behavior (click, shift+click, ctrl/cmd+click) */
  enableMultiRowSelection?: boolean;

  /** Externally controlled selected row ids */
  selectedRowIds?: Array<string | number>;

  /** Executed when selected rows change */
  onSelectedRowIdsChange?: (selectedRowIds: Array<string | number>) => void;

  /** Executed when a row is double-clicked (ignores interactive cell targets) */
  onRowDoubleClick?: (row: any) => void;
}

export const Table = ({
  idLocator,
  className,
  onChangePage,
  onChangeRowsPerPage,
  onFilterBy,
  onRequestSort,
  onSearch,
  onEditCell,
  controlledFilters,
  paginationAlignment = 'left',
  size = 'medium',
  maxHeight,
  height = 'unset',
  cellPadding = '5px 10px',
  columns = [],
  loading = false,
  rows = [],
  rowsCount = -1,
  rowsPerPage = 10,
  rowsPerPageOptions = [5, 10],
  page = 1,
  searchType = '',
  suggestions = [],
  footerHidden = false,
  headerHidden = false,
  localStorageKey,
  stickyFooter = true,
  hover = true,
  hoverForExpandableRow = true,
  enableMultiRowSelection = false,
  selectedRowIds = [],
  onSelectedRowIdsChange,
  onRowDoubleClick,
}: TableProps) => {
  const classesStyle = useTableStyles({ cellPadding, height, maxHeight });

  const expandableRowsCount = rows.filter(
    row => !!row.expandableContent || !!row.expandableRow,
  ).length;

  const [hasFilters, setHasFilters] = useState(
    columns.some(column => column.filters?.some(filter => filter.conditions.some(c => c.value))),
  );

  const setFilters = useCallback(
    filters => {
      if (Array.isArray(filters)) {
        setHasFilters(filters.length !== 0);
      } else {
        setHasFilters(!!filters);
      }

      onFilterBy(filters);
    },
    [onFilterBy],
  );

  const safePage = isNaN(page) ? 0 : page;
  const paginationCount =
    rowsCount >= 0
      ? rowsCount
      : rows.length < rowsPerPage
      ? safePage * rowsPerPage + rows.length
      : rowsCount;
  const paginationMaxPageIndex =
    paginationCount > 0 ? Math.max(0, Math.ceil(paginationCount / rowsPerPage) - 1) : 0;

  return (
    <div className={classesStyle.root}>
      <TableContainer className={classesStyle.tableContainer} component={Paper}>
        <MuiTable className={clsx(classesStyle.table, className)} size={size} stickyHeader>
          {!headerHidden && (
            <TableHead
              actionCell={expandableRowsCount > 0}
              cellPadding={cellPadding}
              columns={columns}
              controlledFilters={controlledFilters}
              localStorageKey={localStorageKey}
              onFilterBy={setFilters}
              onRequestSort={onRequestSort}
              onSearch={onSearch}
              searchType={searchType}
              suggestions={suggestions}
            />
          )}
          <TableBody
            hasFilters={hasFilters}
            columns={columns}
            rows={rows}
            hover={hover}
            hoverForExpandableRows={hoverForExpandableRow}
            rowsPerPage={rowsPerPage}
            loading={loading}
            cellPadding={cellPadding}
            onEditCell={onEditCell}
            idLocator={idLocator}
            hasExpandableRows={expandableRowsCount > 0}
            enableMultiRowSelection={enableMultiRowSelection}
            selectedRowIds={selectedRowIds}
            onSelectedRowIdsChange={onSelectedRowIdsChange}
            onRowDoubleClick={onRowDoubleClick}
          />
          {!footerHidden && (
            <TableFooter className={clsx({ [classesStyle.stickyFooter]: stickyFooter })}>
              <TableRow>
                <TablePagination
                  data-testid="pagination"
                  rowsPerPageOptions={rowsPerPageOptions}
                  count={paginationCount}
                  rowsPerPage={rowsPerPage}
                  page={Math.min(safePage, paginationMaxPageIndex)}
                  onPageChange={(event, page) => {
                    onChangePage(page);
                  }}
                  onRowsPerPageChange={event => {
                    onChangeRowsPerPage(parseInt(event.target.value, 10));
                  }}
                  className={clsx({
                    [classesStyle.leftAlignment]: paginationAlignment === 'left',
                  })}
                  SelectProps={{ 'data-testid': 'rows-per-page' } as any}
                  nextIconButtonProps={{ 'data-testid': 'next-button' } as any}
                  backIconButtonProps={{ 'data-testid': 'previous-button' } as any}
                />
              </TableRow>
            </TableFooter>
          )}
        </MuiTable>
      </TableContainer>
    </div>
  );
};
